<template>
  <div class="reset-password-page">
    <!-- 背景和遮罩層 -->
    <div class="background-overlay"></div>
    <div class="background-image"></div>

    <!-- 主要內容 -->
    <div class="reset-container">
      <!-- 返回按鈕 -->
      <!--<div class="back-button">
        <button @click="handleBack" class="back-btn">
          <i class="fas fa-arrow-left"></i>
        </button>
      </div>-->

      <!-- 標誌和標題 -->
      <div class="header-section">
        <div class="logo">
          <i class="fas fa-heartbeat"></i>
          <span>HealthyAI</span>
        </div>
        <h1 class="page-title">{{ $t("resetPassword.title") }}</h1>
        <p class="page-subtitle">{{ $t("resetPassword.subtitle") }}</p>
      </div>

      <!-- 表單卡片 -->
      <div class="form-card" v-if="!isSuccess">
        <!-- 令牌驗證狀態 -->
        <div v-if="!tokenValidated" class="loading-section">
          <div class="spinner"></div>
          <p>{{ $t("resetPassword.validatingToken") }}</p>
        </div>

        <div v-else-if="tokenError" class="error-section">
          <i class="fas fa-exclamation-triangle error-icon"></i>
          <h3>{{ $t("resetPassword.tokenInvalid") }}</h3>
          <p>{{ $t("resetPassword.tokenExpiredOrInvalid") }}</p>
          <!--<button @click="handleBackToLogin" class="primary-button">
            {{ $t("resetPassword.backToLogin") }}
          </button>-->
        </div>

        <!-- 重設表單 -->
        <div v-else class="reset-form">
          <div class="user-info">
            <i class="fas fa-user-circle user-icon"></i>
            <div class="user-details">
              <p class="user-email">{{ maskedEmail }}</p>
              <p class="token-expiry">
                {{ $t("resetPassword.tokenExpiresIn") }} {{ expiryTime }}
              </p>
            </div>
          </div>

          <form @submit.prevent="handleResetPassword">
            <!-- 新密碼輸入 -->
            <div class="form-group">
              <label for="newPassword">{{
                $t("resetPassword.newPassword")
              }}</label>
              <div class="input-with-icon">
                <i class="fas fa-lock input-icon"></i>
                <input
                  id="newPassword"
                  v-model="form.newPassword"
                  :type="showNewPassword ? 'text' : 'password'"
                  :placeholder="$t('resetPassword.enterNewPassword')"
                  required
                  class="form-input"
                />
                <button
                  type="button"
                  @click="showNewPassword = !showNewPassword"
                  class="password-toggle"
                >
                  <i
                    :class="showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"
                  ></i>
                </button>
              </div>
              <div class="password-requirements">
                <p>{{ $t("resetPassword.passwordRequirements") }}</p>
              </div>
            </div>

            <!-- 確認密碼輸入 -->
            <div class="form-group">
              <label for="confirmPassword">{{
                $t("resetPassword.confirmPassword")
              }}</label>
              <div class="input-with-icon">
                <i class="fas fa-lock input-icon"></i>
                <input
                  id="confirmPassword"
                  v-model="form.confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  :placeholder="$t('resetPassword.confirmNewPassword')"
                  required
                  class="form-input"
                />
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="password-toggle"
                >
                  <i
                    :class="
                      showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'
                    "
                  ></i>
                </button>
              </div>
              <div v-if="passwordMismatch" class="error-message">
                {{ $t("resetPassword.passwordsDoNotMatch") }}
              </div>
            </div>

            <!-- 提交按鈕 -->
            <button
              type="submit"
              :disabled="isSubmitting || passwordMismatch"
              class="submit-button"
              :class="{ loading: isSubmitting }"
            >
              <span v-if="!isSubmitting">
                <i class="fas fa-key"></i>
                {{ $t("resetPassword.resetPassword") }}
              </span>
              <span v-else>
                <div class="button-spinner"></div>
                {{ $t("resetPassword.resetting") }}
              </span>
            </button>
          </form>
        </div>
      </div>

      <!-- 成功狀態 -->
      <div v-else class="success-section">
        <div class="success-card">
          <i class="fas fa-check-circle success-icon"></i>
          <h2>{{ $t("resetPassword.successTitle") }}</h2>
          <p>{{ $t("resetPassword.successMessage") }}</p>
          <!--<button @click="handleBackToLogin" class="success-button">
            <i class="fas fa-sign-in-alt"></i>
            {{ $t("resetPassword.backToLogin") }}
          </button>-->
        </div>
      </div>

      <!-- 底部連結 -->
      <!--<div class="footer-links">
        <button @click="handleBackToLogin" class="text-button">
          <i class="fas fa-arrow-left"></i>
          {{ $t("resetPassword.backToLogin") }}
        </button>
      </div>-->
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, reactive } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import authApi from "@/services/auth";

export default {
  name: "ResetPassword",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();

    // 響應式數據
    const tokenValidated = ref(false);
    const tokenError = ref(false);
    const isSubmitting = ref(false);
    const isSuccess = ref(false);
    const showNewPassword = ref(false);
    const showConfirmPassword = ref(false);
    const userEmail = ref("");
    const tokenExpiry = ref("");

    const form = reactive({
      newPassword: "",
      confirmPassword: "",
    });

    // 計算屬性
    const passwordMismatch = computed(() => {
      return (
        form.newPassword &&
        form.confirmPassword &&
        form.newPassword !== form.confirmPassword
      );
    });

    const maskedEmail = computed(() => {
      if (!userEmail.value) return "";
      const parts = userEmail.value.split("@");
      if (parts.length !== 2) return userEmail.value;

      const username = parts[0];
      const domain = parts[1];

      if (username.length <= 2) {
        return `${"*".repeat(username.length)}@${domain}`;
      }

      return `${username[0]}${"*".repeat(username.length - 2)}${
        username[username.length - 1]
      }@${domain}`;
    });

    const expiryTime = computed(() => {
      if (!tokenExpiry.value) return "";
      const expiry = new Date(tokenExpiry.value);
      const now = new Date();
      const diffMs = expiry - now;
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins <= 0) return t("resetPassword.expired");
      if (diffMins < 60) return `${diffMins}${t("resetPassword.minutes")}`;

      const diffHours = Math.floor(diffMins / 60);
      return `${diffHours}${t("resetPassword.hours")}`;
    });

    // 方法
    const validateToken = async () => {
      const token = route.query.token;
      if (!token) {
        tokenError.value = true;
        tokenValidated.value = true;
        return;
      }

      try {
        // 使用 POST 請求驗證令牌
        const response = await authApi.validateResetToken(token);
        if (response.success && response.data.isValid) {
          tokenExpiry.value = Date.now() + 3600000; // 1小時後過期，實際需要從後端獲取
          tokenValidated.value = true;
        } else {
          tokenError.value = true;
          tokenValidated.value = true;
        }
      } catch (error) {
        console.error("Token validation failed:", error);
        tokenError.value = true;
        tokenValidated.value = true;
      }
    };

    const handleResetPassword = async () => {
      if (passwordMismatch.value) return;

      isSubmitting.value = true;
      const token = route.query.token;

      try {
        const response = await authApi.resetPassword(token, form.newPassword);
        if (response.success) {
          isSuccess.value = true;
        } else {
          alert(t("resetPassword.resetFailed"));
        }
      } catch (error) {
        console.error("Password reset failed:", error);
        alert(t("resetPassword.resetFailed"));
      } finally {
        isSubmitting.value = false;
      }
    };

    const handleBack = () => {
      router.back();
    };

    const handleBackToLogin = () => {
      router.push("/login");
    };

    // 生命週期
    onMounted(() => {
      validateToken();
    });

    return {
      tokenValidated,
      tokenError,
      isSubmitting,
      isSuccess,
      showNewPassword,
      showConfirmPassword,
      form,
      userEmail,
      tokenExpiry,
      passwordMismatch,
      maskedEmail,
      expiryTime,
      validateToken,
      handleResetPassword,
      handleBack,
      handleBackToLogin,
    };
  },
};
</script>

<style scoped>
.reset-password-page {
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.background-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.9) 0%,
    rgba(118, 75, 162, 0.9) 100%
  );
  z-index: -2;
}

.background-image {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url("@/assets/images/background.png");
  background-size: cover;
  background-position: center;
  opacity: 0.1;
  z-index: -1;
}

.reset-container {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
}

.back-button {
  margin-bottom: 20px;
}

.back-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  font-size: 16px;
  cursor: pointer;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateX(-2px);
}

.header-section {
  text-align: center;
  margin-bottom: 40px;
}

.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: white;
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
}

.logo i {
  font-size: 28px;
}

.page-title {
  color: white;
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 12px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.page-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  margin: 0;
}

.form-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.loading-section,
.error-section {
  text-align: center;
  padding: 40px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-icon {
  font-size: 48px;
  color: #e74c3c;
  margin-bottom: 20px;
}

.error-section h3 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.error-section p {
  color: #7f8c8d;
  margin-bottom: 30px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 15px;
  background: #f8f9fa;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 30px;
}

.user-icon {
  font-size: 40px;
  color: #667eea;
}

.user-details {
  flex: 1;
}

.user-email {
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 5px 0;
}

.token-expiry {
  color: #7f8c8d;
  font-size: 14px;
  margin: 0;
}

.form-group {
  margin-bottom: 25px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 15px;
  color: #7f8c8d;
  z-index: 1;
}

.form-input {
  width: 100%;
  padding: 15px 15px 15px 45px;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.3s ease;
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.password-toggle {
  position: absolute;
  right: 15px;
  background: none;
  border: none;
  color: #7f8c8d;
  cursor: pointer;
  padding: 5px;
}

.password-requirements {
  margin-top: 8px;
}

.password-requirements p {
  font-size: 12px;
  color: #7f8c8d;
  margin: 0;
}

.error-message {
  color: #e74c3c;
  font-size: 14px;
  margin-top: 5px;
}

.submit-button {
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.submit-button.loading {
  cursor: wait;
}

.button-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.success-section {
  text-align: center;
}

.success-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 60px 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.success-icon {
  font-size: 64px;
  color: #27ae60;
  margin-bottom: 20px;
}

.success-card h2 {
  color: #2c3e50;
  margin-bottom: 15px;
}

.success-card p {
  color: #7f8c8d;
  margin-bottom: 30px;
  line-height: 1.6;
}

.success-button {
  padding: 15px 30px;
  background: linear-gradient(135deg, #27ae60 0%, #2ecc71 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.success-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(39, 174, 96, 0.3);
}

.footer-links {
  text-align: center;
  margin-top: 30px;
}

.text-button {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.text-button:hover {
  color: rgba(255, 255, 255, 0.8);
  transform: translateX(-5px);
}

.primary-button {
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.primary-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
}

/* 響應式設計 */
@media (max-width: 768px) {
  .reset-container {
    max-width: 100%;
    padding: 0 15px;
  }

  .form-card {
    padding: 30px 20px;
  }

  .page-title {
    font-size: 28px;
  }
}
</style>
